// LogicLine CVI tokens (Stand 17.04.2024) + Flow extensions.
// Brand colors live both here (as a typed module for JS access) and in
// index.css under @theme (for Tailwind utility generation). Keep both in sync.

export const BRAND = {
  blue: "#00599B",
  orange: "#F68121",
  blueDark: "#0D3E63",
  blueGreyDk: "#435D7A",
  blueGrey: "#56779D",
  blueGrey50: "#AABBCE",
  blueGreyLt: "#EDF0F2",
  pikGreyBlue: "#CFD8E0",
  pikLightBlue: "#D3E6F3",
  pikOrange: "#FFC084",
} as const;

export const SCALE = {
  50: "#F6F8FA",
  100: "#EDF0F2",
  150: "#E2E8ED",
  200: "#CFD8E0",
  300: "#AABBCE",
  400: "#7A93AF",
  500: "#56779D",
  600: "#435D7A",
  700: "#0D3E63",
  800: "#0A2F4D",
  900: "#06223A",
} as const;

export type StatusColor =
  | "slate"
  | "blue"
  | "red"
  | "amber"
  | "green"
  | "gray"
  | "purple"
  | "pink"
  | "teal"
  | "yellow"
  | "orange"
  | "lime"
  | "cyan"
  | "indigo"
  | "magenta"
  | "brown";

export interface StatusTint {
  bg: string;
  fg: string;
  dot: string;
  label: string;
}

export const STATUS_PALETTE: Record<StatusColor, StatusTint> = {
  slate: { bg: "#E8ECF0", fg: "#3F4A57", dot: "#7A8597", label: "Not started" },
  blue: { bg: "#DCEAF6", fg: "#1F4D78", dot: "#3D7FB8", label: "In progress" },
  // fg is a FIXED dark hue (not var(--danger)/var(--warn) — those flip LIGHT in dark
  // mode, giving low-contrast "orange-on-orange"/"red-on-red" text on the chip).
  red: { bg: "#F5DDDB", fg: "#7E2F26", dot: "#B85249", label: "Blocked" },
  amber: { bg: "#F7E5C1", fg: "#6E4A12", dot: "#B78029", label: "Review" },
  green: { bg: "#D9E8D5", fg: "#34593C", dot: "#5E8B5F", label: "Done" },
  gray: { bg: "#E4E4E4", fg: "#4A4A4A", dot: "#8A8A8A", label: "On hold" },
  purple: { bg: "#E4DCEF", fg: "#4D3776", dot: "#7B5AA8", label: "Discovery" },
  pink: { bg: "#F3DCE4", fg: "#7A2E4A", dot: "#B45B7C", label: "Customer" },
  teal: { bg: "#D6E8E5", fg: "#1F5E55", dot: "#3F8C7E", label: "Research" },
  yellow: { bg: "#F2EBC0", fg: "#6A5C12", dot: "#A89629", label: "Pending" },
  // Six added hues (2026-09) for more differentiation — placed in the widest gaps
  // on the wheel so a board no longer has to reuse a colour (fg is a FIXED dark hue
  // legible on the light bg; dark mode derives its chip from `dot` via
  // .flow-status-tint, so a saturated dot is what keeps dark legible).
  orange: { bg: "#F8DFC6", fg: "#7C3D0E", dot: "#D2701E", label: "Orange" },
  lime: { bg: "#E6EFC5", fg: "#4B591A", dot: "#8CA82E", label: "Lime" },
  cyan: { bg: "#CFEAF0", fg: "#16535F", dot: "#2C9CB5", label: "Cyan" },
  indigo: { bg: "#DEE0F5", fg: "#2E3576", dot: "#5566C8", label: "Indigo" },
  magenta: { bg: "#F1D8EC", fg: "#6E1F63", dot: "#B23F9E", label: "Magenta" },
  brown: { bg: "#E9DED2", fg: "#5A4128", dot: "#977A54", label: "Brown" },
};

// The full swatch list for status colour pickers, derived from the palette so the
// picker can never drift out of sync (it used to be a hand-typed 8-name array that
// silently omitted gray + yellow). Order = insertion order in STATUS_PALETTE.
export const STATUS_COLORS = Object.keys(STATUS_PALETTE) as StatusColor[];

export type AvatarColor = "violet" | "blue" | "amber" | "green" | "pink" | "teal" | "slate";

export const AVATAR_COLORS: Record<AvatarColor, string> = {
  violet: "#7A5AE0",
  blue: "#3D7FB8",
  amber: "#D9892B",
  green: "#5E8B5F",
  pink: "#B45B7C",
  teal: "#3F8C7E",
  slate: "#5E6A7C",
};

export const TAG_COLORS = [
  "#E5E9EE",
  "#D7E5F2",
  "#E5DDEF",
  "#EFE2D7",
  "#DDE9DC",
  "#F0E0E5",
  "#DCE9E6",
  "#EBE9D4",
] as const;

export function tagColor(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const idx = h % TAG_COLORS.length;
  return TAG_COLORS[idx] ?? TAG_COLORS[0]!;
}

export const HAIRLINE = {
  base: "rgba(13, 62, 99, 0.10)",
  soft: "rgba(13, 62, 99, 0.06)",
  strong: "rgba(13, 62, 99, 0.16)",
} as const;

export function statusColorOf(color: string | undefined | null): StatusTint {
  if (!color) return STATUS_PALETTE.slate;
  return (STATUS_PALETTE as Record<string, StatusTint>)[color] ?? STATUS_PALETTE.slate;
}

export function avatarColorOf(color: string | undefined | null): string {
  if (!color) return AVATAR_COLORS.slate;
  return (AVATAR_COLORS as Record<string, string>)[color] ?? AVATAR_COLORS.slate;
}
